// The private insurer's part of the bill (docs/plans/phase2-private-insurance.md §6 item 3):
// what the insurer and the patient pay, the benefits that moved money to the patient, the
// services waiting for the insurer's approval with the approvals recorded, and the claim.

import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { formatMoney, INSURER_GATE_WORDS, moneyFromCents } from '@wodi/domain';
import type { InsurerPreauthView, VisitInsuranceView } from '@wodi/shared';
import {
  Button,
  DateField,
  Dialog,
  DialogActions,
  LoadingBlock,
  MoneyField,
  Notice,
  RadioGroup,
  Tag,
  TextField,
  type TagState,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  decidePreauth,
  insuranceKeys,
  prepareInsurerClaim,
  recordPreauth,
  visitInsuranceQuery,
} from '../api/insurance.ts';
import { can, sessionQuery } from '../api/session.ts';
import billing from '../billing/billing.module.css';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { coverLine } from './cover.tsx';
import styles from './insurance.module.css';

const kes = (cents: string) => formatMoney(moneyFromCents(BigInt(cents)));

const GATE_TAG: Record<VisitInsuranceView['approvals'][number]['state'], TagState> = {
  not_required: 'neutral',
  needs_request: 'attention',
  waiting: 'pending',
  approved: 'done',
  declined: 'failed',
  expired: 'attention',
};

export const PREAUTH_STATE_WORDS: Record<InsurerPreauthView['state'], string> = {
  requested: 'Waiting for the insurer',
  approved: 'Approved',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

function RecordRequestDialog({ view, onClose }: { view: VisitInsuranceView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const services = view.approvals.filter((a) => a.state !== 'approved');
  const [serviceItemId, setServiceItemId] = useState<string>(services[0]?.serviceItemId ?? 'visit');
  const [amount, setAmount] = useState<bigint | null>(null);
  const save = useMutation({
    mutationFn: () =>
      recordPreauth(view.visitId, {
        serviceItemId: serviceItemId === 'visit' ? null : serviceItemId,
        requested: amount === null ? null : { currency: 'KES', cents: amount.toString() },
      }),
    onSuccess: async () => {
      toasts.show({ title: 'Approval request recorded' });
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.visit(view.visitId) });
      onClose();
    },
  });
  return (
    <Dialog
      title="Record an approval request"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <form
        className={styles.form}
        aria-label="Record an approval request"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <p className={styles.detail}>
          {`Ask ${view.cover?.payerName ?? 'the insurer'} by its usual channel (phone, email or portal), then record it here.`}
        </p>
        <RadioGroup
          label="For"
          value={serviceItemId}
          onChange={setServiceItemId}
          options={[
            ...services.map((s) => ({ id: s.serviceItemId, label: s.itemName })),
            { id: 'visit', label: 'The whole visit' },
          ]}
        />
        <MoneyField
          label="Amount asked for"
          description="Optional."
          value={amount}
          onChange={(cents) => {
            setAmount(cents);
          }}
        />
        {save.error === null ? null : (
          <Notice tone="error">{errorText(save.error, "The request couldn't be recorded.")}</Notice>
        )}
        <DialogActions>
          <Button variant="quiet" onPress={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isPending={save.isPending} pendingLabel="Saving…">
            Record request
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function AnswerDialog({
  visitId,
  preauth,
  onClose,
}: {
  visitId: string;
  preauth: InsurerPreauthView;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<'approved' | 'declined'>('approved');
  const [reference, setReference] = useState('');
  const [approved, setApproved] = useState<bigint | null>(
    preauth.requested === null ? null : BigInt(preauth.requested.cents),
  );
  const [validUntil, setValidUntil] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const missing =
    outcome === 'approved'
      ? reference.trim() === '' || approved === null || approved <= 0n
      : reason.trim().length < 2;
  const save = useMutation({
    mutationFn: () =>
      decidePreauth(
        preauth.id,
        outcome === 'approved'
          ? {
              outcome,
              expectedVersion: preauth.version,
              insurerReference: reference.trim(),
              approved: { currency: 'KES', cents: (approved ?? 0n).toString() },
              ...(validUntil === null ? {} : { validUntil }),
            }
          : {
              outcome,
              expectedVersion: preauth.version,
              reason: reason.trim(),
              ...(reference.trim() === '' ? {} : { insurerReference: reference.trim() }),
            },
      ),
    onSuccess: async () => {
      toasts.show({ title: 'Insurer’s answer recorded' });
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.visit(visitId) });
      onClose();
    },
  });
  return (
    <Dialog
      title="Record the insurer’s answer"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <form
        className={styles.form}
        aria-label="Record the insurer’s answer"
        onSubmit={(e) => {
          e.preventDefault();
          setTried(true);
          if (!missing) save.mutate();
        }}
      >
        <RadioGroup
          label="The insurer"
          orientation="horizontal"
          value={outcome}
          onChange={(v) => {
            setOutcome(v === 'declined' ? 'declined' : 'approved');
          }}
          options={[
            { id: 'approved', label: 'Approved' },
            { id: 'declined', label: 'Declined' },
          ]}
        />
        <TextField
          label="Insurer’s reference"
          description={outcome === 'approved' ? 'The approval code.' : 'Optional.'}
          value={reference}
          onChange={setReference}
          {...(tried && outcome === 'approved' && reference.trim() === ''
            ? { errorMessage: 'Enter the approval code the insurer gave.' }
            : {})}
        />
        {outcome === 'approved' ? (
          <div className={styles.fields}>
            <MoneyField
              label="Amount approved"
              value={approved}
              onChange={(cents) => {
                setApproved(cents);
              }}
              {...(tried && (approved === null || approved <= 0n)
                ? { errorMessage: 'Enter the amount the insurer approved.' }
                : {})}
            />
            <DateField
              label="Valid until"
              description="Optional. Included."
              value={validUntil}
              onChange={setValidUntil}
            />
          </div>
        ) : (
          <TextField
            label="Insurer’s reason"
            value={reason}
            onChange={setReason}
            {...(tried && reason.trim().length < 2
              ? { errorMessage: 'Enter the reason the insurer gave.' }
              : {})}
          />
        )}
        {save.error === null ? null : (
          <Notice tone="error">{errorText(save.error, "The answer couldn't be recorded.")}</Notice>
        )}
        <DialogActions>
          <Button variant="quiet" onPress={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isPending={save.isPending} pendingLabel="Saving…">
            Record answer
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export function BillInsuranceSummary({ visitId }: { visitId: string }) {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const view = useQuery(visitInsuranceQuery(visitId));
  const [asking, setAsking] = useState(false);
  const [answering, setAnswering] = useState<InsurerPreauthView | null>(null);
  const prepare = useMutation({
    mutationFn: () => prepareInsurerClaim(visitId),
    onSuccess: async ({ claimId }) => {
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.visit(visitId) });
      await navigate({ to: '/insurance/claims/$claimId', params: { claimId } });
    },
  });
  const mayRecord = session !== null && can(session, 'insurance.preauth.manage');
  const mayClaim = session !== null && can(session, 'insurance.claims.manage');
  const mayReadClaims = session !== null && can(session, 'insurance.claims.read');

  return (
    <section className={billing.section} aria-labelledby="bill-insurance">
      <h2 id="bill-insurance" className={billing.sectionHeading}>
        Insurance
      </h2>
      {view.isPending ? <LoadingBlock label="Loading the insurance" rows={2} /> : null}
      {view.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void view.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(view.error, "The insurance part of the bill couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {view.isSuccess && view.data.cover !== null ? (
        <div className={styles.stack}>
          <p className={styles.sentence}>
            {`${view.data.cover.payerName} pays ${kes(view.data.payerAmount.cents)}. Patient pays ${kes(view.data.patientAmount.cents)}.`}
          </p>
          <p className={styles.detail}>
            {coverLine(view.data.cover)}
            {', member '}
            <span className={styles.id}>{view.data.cover.memberNumber}</span>
          </p>
          {view.data.benefits.length > 0 ? (
            <ul className={styles.list} aria-label="Benefits applied">
              {view.data.benefits.map((b) => (
                <li key={`${b.chargeId}-${b.ruleSentence}`} className={styles.row}>
                  {`${b.itemName}: ${b.kind === 'excess' ? 'excess' : 'limit reached'}, ${kes(b.shifted.cents)} moved to the patient (${b.ruleSentence}).`}
                </li>
              ))}
            </ul>
          ) : null}
          {view.data.approvals.length > 0 || view.data.preauths.length > 0 ? (
            <div className={styles.stack}>
              <h3 className={billing.sectionHeading}>Insurer approvals</h3>
              <ul className={styles.list} aria-label="Services that need the insurer’s approval">
                {view.data.approvals.map((a) => (
                  <li key={a.chargeId} className={styles.row}>
                    <span className={styles.grow}>{a.itemName}</span>
                    <Tag state={GATE_TAG[a.state]}>{INSURER_GATE_WORDS[a.state]}</Tag>
                  </li>
                ))}
              </ul>
              {view.data.preauths.length > 0 ? (
                <ul className={styles.list} aria-label="Approval requests">
                  {view.data.preauths.map((p) => (
                    <li key={p.id} className={styles.row}>
                      <span className={styles.grow}>
                        {`${p.serviceName ?? 'The whole visit'}, asked ${formatInstant(p.requestedAt)}`}
                        {p.insurerReference === null ? null : (
                          <>
                            {', reference '}
                            <span className={styles.id}>{p.insurerReference}</span>
                          </>
                        )}
                        {p.approved === null ? '' : `, ${kes(p.approved.cents)} approved`}
                        {p.decisionNote === null ? '' : `: ${p.decisionNote}`}
                      </span>
                      <Tag
                        state={
                          p.state === 'approved'
                            ? 'done'
                            : p.state === 'requested'
                              ? 'pending'
                              : 'neutral'
                        }
                      >
                        {PREAUTH_STATE_WORDS[p.state]}
                      </Tag>
                      {mayRecord && p.state === 'requested' ? (
                        <Button
                          dense
                          isDisabled={!online}
                          onPress={() => {
                            setAnswering(p);
                          }}
                        >
                          Record answer
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          <div className={styles.actions}>
            {mayRecord ? (
              <Button
                isDisabled={!online}
                onPress={() => {
                  setAsking(true);
                }}
              >
                Record an approval request
              </Button>
            ) : null}
            {view.data.claim !== null && mayReadClaims ? (
              <Link to="/insurance/claims/$claimId" params={{ claimId: view.data.claim.id }}>
                {`Claim `}
                <span className={styles.id}>{view.data.claim.claimNumber}</span>
                {`: ${view.data.claim.sentence}`}
              </Link>
            ) : null}
            {view.data.claim === null && mayClaim ? (
              <Button
                isDisabled={!online}
                isPending={prepare.isPending}
                pendingLabel="Preparing…"
                onPress={() => {
                  prepare.mutate();
                }}
              >
                Prepare insurer claim
              </Button>
            ) : null}
          </div>
          {prepare.error === null ? null : (
            <Notice tone="error">
              {prepare.error instanceof ApiError
                ? prepare.error.message
                : "The claim couldn't be prepared. Try again."}
            </Notice>
          )}
        </div>
      ) : null}
      {asking && view.data !== undefined ? (
        <RecordRequestDialog
          view={view.data}
          onClose={() => {
            setAsking(false);
          }}
        />
      ) : null}
      {answering === null ? null : (
        <AnswerDialog
          visitId={visitId}
          preauth={answering}
          onClose={() => {
            setAnswering(null);
          }}
        />
      )}
    </section>
  );
}
